import { TaskSourceType } from "src/api/generated/postgres/db/types/tasks/enums/taskSourceType.ts";
import type { OpenGenericDialogFunc } from "src/components/common/dialogs/GenericDialogContext.ts";
import { ProjectActivityApi } from "src/api/generated/erp/project/projectActivity/api/projectActivityApi.ts";
import { SingleReclamationView } from "../../erp/reclamation/singleReclamationView/SingleReclamationView.tsx";
import { ProjectActivitiesContainerView } from "src/components/views/erp/project/projectActivity/ProjectActivitiesContainerView.tsx";
import i18n from "i18next";
import { CustomerOrdersPage } from "src/components/views/erp/sales/customerOrder/CustomerOrdersPage.tsx";
import { WorkspacesView } from "src/components/views/workspaces/WorkspacesView.tsx";
import { ShopOrdersView } from "src/components/views/erp/production/shopOrder/ShopOrdersView.tsx";
import { ProductChangeRequestDialog } from "src/components/views/erp/productChanges/requests/ProductChangeRequestDialog.tsx";
import { ProductChangeDialog } from "src/components/views/erp/productChanges/changes/ProductChangeDialog.tsx";
import { SingleCustomerOfferView } from "src/components/views/erp/sales/customerOrderOffer/SingleCustomerOfferView.tsx";
import { defineAgentUiContextKey } from "src/components/views/ai/assistant/uiContext/agentUiContextKey.ts";
import { SafetyControlSurveysPage } from "src/components/views/erp/safetyControl/SafetyControlSurveysPage.tsx";
import { SafetyControlSurveyQueryApi } from "src/api/generated/erp/safetyControlSurvey/api/safetyControlSurveyQueryApi.ts";
import { ProcessImprovementsView } from "src/components/views/spc/processImprovement/ProcessImprovementsView.tsx";
import { CustomersView } from "src/components/views/erp/sales/customer/CustomersView.tsx";

export interface TaskSourceActions {
	openSource: (params: OpenTaskSourceParams) => Promise<void>;
}

export interface OpenTaskSourceParams {
	sourceId: number;
	openGenericDialog: OpenGenericDialogFunc;
}

export const getTaskSourceActions = (sourceType: TaskSourceType): TaskSourceActions => {
	switch (sourceType) {
		case "PROJECT_ACTIVITY":
			return projectActivityTaskSourceActions;

		case "CUSTOMER_ORDER":
			return customerOrderTaskSourceActions;

		case "CUSTOMER_ORDER_OFFER":
			return customerOrderOfferTaskSourceActions;

		case "SAFETY_CONTROL_SURVEY":
			return safetyControlSurveyTaskSourceActions;

		case "RECLAMATION":
			return reclamationTaskSourceActions;

		case "PROCESS_IMPROVEMENT":
			return processImprovementTaskSourceActions;

		case "WORKSPACE":
			return workspaceTaskSourceActions;

		case "SHOP_ORDER":
			return shopOrderTaskSourceActions;

		case "PRODUCT_CHANGE_REQUEST":
			return productChangeRequestTaskSourceActions;

		case "PRODUCT_CHANGE":
			return productChangeTaskSourceActions;

		case "CUSTOMER":
			return customerTaskSourceActions;
	}
};

const projectActivityTaskSourceActions: TaskSourceActions = {
	openSource: async ({ sourceId, openGenericDialog }) => {
		const activity = await ProjectActivityApi.getSingleActivity({
			activityId: sourceId,
		});
		openGenericDialog({
			title: i18n.t("activity"),
			agentContext: {
				key: defineAgentUiContextKey("tasks.source.projectActivity.dialog"),
				data: { projectActivityId: sourceId },
				pathSegments: [`${i18n.t("activity")} ${sourceId}`],
			},
			content: (
				<ProjectActivitiesContainerView
					subProjectOrActivityId={{ type: "activity", id: activity.activityId }}
				/>
			),
		});
	},
};

const customerOrderTaskSourceActions: TaskSourceActions = {
	openSource: async ({ sourceId, openGenericDialog }) => {
		openGenericDialog({
			title: i18n.t("customer_order"),
			agentContext: {
				key: defineAgentUiContextKey("tasks.source.customerOrder.dialog"),
				data: { customerOrderId: sourceId },
				pathSegments: [`${i18n.t("customer_order")} ${sourceId}`],
			},
			content: <CustomerOrdersPage onlyCustomerOrderToShowId={sourceId} />,
		});
	},
};

const customerOrderOfferTaskSourceActions: TaskSourceActions = {
	openSource: async ({ sourceId, openGenericDialog }) => {
		openGenericDialog({
			title: i18n.t("customer_order_offer"),
			agentContext: {
				key: defineAgentUiContextKey("tasks.source.customerOrderOffer.dialog"),
				data: { customerOrderOfferId: sourceId },
				pathSegments: [`${i18n.t("offer")} ${sourceId}`],
			},
			content: <SingleCustomerOfferView customerOrderOfferId={sourceId} />,
		});
	},
};

const safetyControlSurveyTaskSourceActions: TaskSourceActions = {
	openSource: async ({ sourceId, openGenericDialog }) => {
		const survey = await SafetyControlSurveyQueryApi.getSurvey({
			safetyControlSurveyId: sourceId,
		});
		openGenericDialog({
			title: i18n.t("safety_control_survey"),
			agentContext: {
				key: defineAgentUiContextKey("tasks.source.safetyControlSurvey.dialog"),
				data: { safetyControlSurveyId: sourceId },
				pathSegments: [`${i18n.t("safety_control_survey")} ${sourceId}`],
			},
			content: (
				<SafetyControlSurveysPage
					isActive={survey.safetyControlSurveyState !== "CLOSED"}
					onlySafetyControlSurveyToShowId={sourceId}
				/>
			),
		});
	},
};

const processImprovementTaskSourceActions: TaskSourceActions = {
	openSource: async ({ sourceId, openGenericDialog }) => {
		openGenericDialog({
			title: i18n.t("explanations"),
			size: "xl",
			agentContext: {
				key: defineAgentUiContextKey("tasks.source.processImprovement.dialog"),
				data: { processImprovementId: sourceId },
				pathSegments: [`${i18n.t("explanation")} ${sourceId}`],
			},
			content: (
				<ProcessImprovementsView onlyProcessImprovementId={sourceId} selectFirstImprovementOnLoad={true} />
			),
		});
	},
};

const workspaceTaskSourceActions: TaskSourceActions = {
	openSource: async ({ sourceId, openGenericDialog }) => {
		openGenericDialog({
			title: i18n.t("workspace"),
			agentContext: {
				key: defineAgentUiContextKey("tasks.source.workspace.dialog"),
				data: { workspaceId: sourceId },
				pathSegments: [`${i18n.t("workspace")} ${sourceId}`],
			},
			content: <WorkspacesView onlyWorkspaceIdToShow={sourceId} />,
		});
	},
};

const reclamationTaskSourceActions: TaskSourceActions = {
	openSource: async ({ sourceId, openGenericDialog }) => {
		openGenericDialog(() => ({
			title: i18n.t("reclamation"),
			agentContext: {
				key: defineAgentUiContextKey("tasks.source.reclamation.dialog"),
				data: { reclamationId: sourceId },
				pathSegments: [`${i18n.t("reclamation")} ${sourceId}`],
			},
			content: <SingleReclamationView reclamationId={sourceId} />,
		}));
	},
};

const shopOrderTaskSourceActions: TaskSourceActions = {
	openSource: async ({ sourceId, openGenericDialog }) => {
		openGenericDialog({
			title: i18n.t("shop_order"),
			agentContext: {
				key: defineAgentUiContextKey("tasks.source.shopOrder.dialog"),
				data: { shopOrderId: sourceId },
				pathSegments: [`${i18n.t("shop_order")} ${sourceId}`],
			},
			content: <ShopOrdersView onlyShopOrderId={sourceId} />,
		});
	},
};

const productChangeRequestTaskSourceActions: TaskSourceActions = {
	openSource: async ({ sourceId, openGenericDialog }) => {
		openGenericDialog(({ closeDialog, onContentEdited }) => ({
			title: i18n.t("product_change_request"),
			agentContext: {
				key: defineAgentUiContextKey("tasks.source.productChangeRequest.dialog"),
				data: { productChangeRequestId: sourceId },
				pathSegments: [`${i18n.t("product_change_request")} ${sourceId}`],
			},
			content: (
				<ProductChangeRequestDialog
					productChangeRequestId={sourceId}
					mode={"manage"}
					onFormEdited={onContentEdited}
					onCompleted={async () => {
						await closeDialog();
					}}
				/>
			),
		}));
	},
};

const productChangeTaskSourceActions: TaskSourceActions = {
	openSource: async ({ sourceId, openGenericDialog }) => {
		openGenericDialog(({ onContentEdited }) => ({
			title: i18n.t("product_change"),
			agentContext: {
				key: defineAgentUiContextKey("tasks.source.productChange.dialog"),
				data: { productChangeId: sourceId },
				pathSegments: [`${i18n.t("product_change")} ${sourceId}`],
			},
			content: (
				<ProductChangeDialog
					productChangeId={sourceId}
					onFormEdited={onContentEdited}
					onCompleted={async () => {}}
				/>
			),
		}));
	},
};

const customerTaskSourceActions: TaskSourceActions = {
	openSource: async ({ sourceId, openGenericDialog }) => {
		openGenericDialog({
			title: i18n.t("customer"),
			agentContext: {
				key: defineAgentUiContextKey("tasks.source.customer.dialog"),
				data: { customerId: sourceId },
				pathSegments: [`${i18n.t("customer")} ${sourceId}`],
			},
			content: <CustomersView onlyCustomerToShowId={sourceId} />,
		});
	},
};
